import React from "react";
import "./primitives.scss";

export const SectionHeader = ({ title, subtitle, right }) => (
  <div className="ysd-section-header">
    <div className="ysd-section-header__text">
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {right && <div className="ysd-section-header__right">{right}</div>}
  </div>
);

// `icon` accepted for back-compat with older call sites but no longer rendered —
// emoji glyphs were noisy in dense KPI grids. Sharpness comes from typography.
export const KpiCard = ({ label, value, delta, trend, accent = "brand", hint }) => (
  <div className={`ysd-kpi ysd-kpi--${accent}`}>
    <span className="ysd-kpi__label">{label}</span>
    <div className="ysd-kpi__value-row">
      <span className="ysd-kpi__value">{value}</span>
      {typeof delta !== "undefined" && delta !== null && delta !== "" && (
        <span className={`ysd-kpi__delta ysd-kpi__delta--${trend || "flat"}`}>
          {trend === "up" ? "▲" : trend === "down" ? "▼" : ""} {delta}
        </span>
      )}
    </div>
    {hint && <span className="ysd-kpi__hint">{hint}</span>}
  </div>
);

export const ChartCard = ({ title, subtitle, right, children, span = 1, minHeight = 260, ...rest }) => (
  // `...rest` lets callers pass arbitrary attributes (e.g. data-anomaly-panel
  // for the AnomaliesCard "investigate" jumps) onto the card without needing a
  // wrapper div — wrappers break the grid math because gridColumn lives on
  // this element.
  <div {...rest} className="ysd-card" style={{ gridColumn: `span ${span}` }}>
    <div className="ysd-card__head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {right && <div className="ysd-card__right">{right}</div>}
    </div>
    <div className="ysd-card__body" style={{ minHeight }}>{children}</div>
  </div>
);

export const Badge = ({ tone = "neutral", children, dot, title }) => (
  <span className={`ysd-badge ysd-badge--${tone}`} title={title}>
    {dot && <i className="ysd-badge__dot" />}
    {children}
  </span>
);


export const Pill = ({ active, onClick, children }) => (
  <button
    type="button"
    className={`ysd-pill ${active ? "ysd-pill--active" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

export const EmptyHint = ({ children }) => (
  <div className="ysd-empty">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      <path d="m16 2 2 2 4-4" />
      <path d="M12 7v5l3 3" />
    </svg>
    <div className="ysd-empty__text">{children || "No data available."}</div>
  </div>
);


export const ErrorHint = ({ message, onRetry }) => (
  <div className="ysd-error">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <div className="ysd-error__text">{message || "Failed to load data"}</div>
    {onRetry && (
      <button className="ysd-error-retry" onClick={onRetry}>
        Retry
      </button>
    )}
  </div>
);


export const Loader = ({ message, minHeight = 360 }) => (
  <div className="ysd-loader" style={{ minHeight }}>
    <div className="ysd-loader__spinner" />
    {message && <div className="ysd-loader__text">{message}</div>}
  </div>
);

// A thin, dependency-free sparkline used inside KPI cards.
export const Sparkline = ({ points = [], width = 92, height = 28, stroke = "var(--text-highlight)" }) => {
  if (!points.length) return null;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / Math.max(1, points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(height - ((p - min) / range) * height).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} className="ysd-spark">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
};

export const riskTone = (risk) =>
  risk === "high" ? "danger" : risk === "medium" ? "warning" : risk === "low" ? "success" : "neutral";
